import { useEffect, useRef, useState, type FormEvent } from 'react';
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate } from 'react-router-dom';
import { differenceInCalendarDays, parseISO, startOfDay } from 'date-fns';
import { Button } from '@/shared/ui/Button';
import { Input } from '@/shared/ui/Input';
import { Icon } from '@/shared/ui/Icon';
import { StatusBadge } from '@/shared/ui/StatusBadge';
import { SkeletonList } from '@/shared/ui/Skeleton';
import { ErrorState } from '@/shared/ui/ErrorState';
import { Dialog } from '@/shared/ui/Dialog';
import { DragScroll } from '@/shared/ui/DragScroll';
import { useToast } from '@/shared/ui/Toast';
import { Rise } from '@/shared/ui/Rise';
import { createMeeting, listMeetingsPage, type MeetingListItem } from '@/shared/api/meetings';
import { listRooms } from '@/shared/api/rooms';
import { fmtKst } from '@/shared/lib/date';
import { HOT_REGIONS, orderByFavorites } from '@/features/place/regions';
import { usePreferredRegions } from '@/features/place/usePreferredRegions';

/** 오늘 이후로 날짜가 확정된 모임 중 가장 가까운 것 — 토스 홈의 '다음 일정' 문법 */
function findNextMeeting(items: readonly MeetingListItem[]): MeetingListItem | null {
  const today = startOfDay(new Date());
  const upcoming = items
    .filter((m) => m.confirmedDate && parseISO(m.confirmedDate) >= today)
    .sort((a, b) => parseISO(a.confirmedDate as string).getTime() - parseISO(b.confirmedDate as string).getTime());
  return upcoming[0] ?? null;
}

function dDayLabel(dateIso: string): string {
  const diff = differenceInCalendarDays(parseISO(dateIso), new Date());
  if (diff <= 0) return '오늘';
  if (diff === 1) return '내일';
  return `D-${diff}`;
}

function meetingIconTone(status: MeetingListItem['status']): string {
  if (status === 'VOTING') return 'bg-warning-soft text-warning-strong';
  if (status === 'SETTLING') return 'bg-error-soft text-error';
  return 'bg-primary-soft text-primary';
}

function meetingIconName(status: MeetingListItem['status']): 'cal' | 'clock' | 'coin' {
  if (status === 'VOTING') return 'clock';
  if (status === 'SETTLING') return 'coin';
  return 'cal';
}

export default function HomePage() {
  const qc = useQueryClient();
  const toast = useToast();
  const navigate = useNavigate();
  const [creating, setCreating] = useState(false);
  const [title, setTitle] = useState('');
  const [region, setRegion] = useState('');
  const [titleError, setTitleError] = useState<string | null>(null);
  const { regions: preferredRegions, toggle: togglePreferredRegion } = usePreferredRegions();

  // 모임 지역으로 칩을 누르면 입력값 채우고, 동시에 '주로 가는 지역'으로 등록한다.
  function pickRegionChip(name: string): void {
    setRegion(name);
    if (!preferredRegions.includes(name)) togglePreferredRegion(name);
  }

  const meetingsQ = useInfiniteQuery({
    queryKey: ['meetings'],
    queryFn: ({ pageParam }) => listMeetingsPage(pageParam, 20),
    initialPageParam: null as string | null,
    getNextPageParam: (last) => last.nextCursor,
  });

  const sentinelRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    if (!meetingsQ.hasNextPage) return;
    const target = sentinelRef.current;
    if (!target) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting && !meetingsQ.isFetchingNextPage) {
          meetingsQ.fetchNextPage();
        }
      },
      { threshold: 0.1 },
    );
    observer.observe(target);
    return () => observer.disconnect();
  }, [meetingsQ.hasNextPage, meetingsQ.isFetchingNextPage, meetingsQ.fetchNextPage]);

  // 모임방은 홈의 섹션으로만 노출 — 방이 없는 첫 사용자에게는 개념 자체를 보여주지 않는다.
  // 첫 모임방은 모임 상세의 멤버 시트('이 멤버로 모임방 만들기')에서 자연스럽게 만들어진다.
  const roomsQ = useQuery({ queryKey: ['rooms'], queryFn: listRooms, staleTime: 30_000 });
  const rooms = roomsQ.data ?? [];

  const items = meetingsQ.data?.pages.flatMap((p) => p.items) ?? [];
  const nextMeeting = findNextMeeting(items);
  // 하이라이트로 올라간 모임은 리스트에서 빼 중복 노출 방지
  const restItems = nextMeeting ? items.filter((m) => m.id !== nextMeeting.id) : items;

  const createMut = useMutation({
    mutationFn: () => {
      const trimmedRegion = region.trim();
      return createMeeting(
        trimmedRegion ? { title: title.trim(), region: trimmedRegion } : { title: title.trim() },
      );
    },
    onSuccess: (created) => {
      qc.invalidateQueries({ queryKey: ['meetings'] });
      setCreating(false);
      setTitle('');
      setRegion('');
      toast.success('새 모임을 만들었어요. 친구를 초대해보세요.');
      // 생성 직후 다음 행동(초대·투표)은 상세 화면에 있다
      navigate(`/meetings/${created.id}`);
    },
    onError: () => toast.error('모임 생성에 실패했어요.'),
  });

  function onSubmit(e: FormEvent) {
    e.preventDefault();
    const trimmed = title.trim();
    if (trimmed.length === 0) {
      setTitleError('모임 제목을 입력해주세요.');
      return;
    }
    if (trimmed.length > 50) {
      setTitleError('제목은 50자까지 가능해요.');
      return;
    }
    setTitleError(null);
    createMut.mutate();
  }

  const summaryLine =
    !meetingsQ.data || items.length === 0
      ? '친구와의 약속을 한 곳에서'
      : nextMeeting
        ? `다음 모임까지 ${dDayLabel(nextMeeting.confirmedDate as string)}`
        : `${items.length}개의 모임이 진행 중이에요`;

  return (
    <section className="space-y-7 pb-2" aria-label="모임 홈">
      <header className="flex items-end justify-between gap-3 pt-2">
        <div className="min-w-0">
          <p className="text-[12.5px] font-semibold tracking-[-0.1px] text-primary-text">
            {summaryLine}
          </p>
          <h1 className="mt-1 text-[28px] font-bold leading-[1.05] tracking-[-0.8px] text-ink">
            모임
          </h1>
        </div>
        {meetingsQ.data && items.length > 0 && (
          <button
            type="button"
            onClick={() => setCreating(true)}
            aria-label="새 모임 만들기"
            className="inline-flex min-h-10 shrink-0 items-center gap-1.5 rounded-full border border-primary/25 bg-primary-soft/70 px-3.5 py-2 text-[13.5px] font-semibold text-primary-text transition-colors hover:bg-primary-soft focus-visible:outline-none focus-visible:shadow-focus-ring"
          >
            <Icon name="plus" size={14} />
            새 모임
          </button>
        )}
      </header>

      {meetingsQ.isLoading && <SkeletonList count={3} />}

      {meetingsQ.isError && (
        <ErrorState title="모임을 불러오지 못했어요" onRetry={() => meetingsQ.refetch()} />
      )}

      {meetingsQ.data && items.length === 0 && (
        <Rise>
        <div className="pt-2">
          <h2 className="text-[22px] font-bold leading-tight tracking-[-0.5px] text-ink">
            모임 하나로<br />약속이 끝나요
          </h2>
          <p className="mt-2 text-[14px] font-medium leading-[1.5] text-ink-muted">
            단톡방에서 며칠 걸리던 날짜·장소 정하기, 여기선 세 단계면 돼요.
          </p>
          <ol className="mt-6 space-y-1">
            {[
              { step: '1', title: '모임 만들기', desc: '제목만 정하면 10초면 돼요' },
              { step: '2', title: '초대 링크 공유', desc: '친구는 링크만 누르면 바로 합류해요' },
              { step: '3', title: '투표로 확정', desc: '날짜·장소를 투표로 정하고 정산까지 한 번에' },
            ].map((s) => (
              <li key={s.step} className="flex items-center gap-4 py-2.5">
                <span
                  aria-hidden="true"
                  className="flex h-10 w-10 shrink-0 items-center justify-center rounded-[14px] bg-[image:var(--hero-grad)] text-[15px] font-bold text-white shadow-[var(--hero-glow)]"
                >
                  {s.step}
                </span>
                <span className="min-w-0">
                  <span className="block text-[15.5px] font-semibold tracking-[-0.2px] text-ink">{s.title}</span>
                  <span className="mt-0.5 block text-[13px] font-medium text-ink-muted">
                    {s.desc}
                  </span>
                </span>
              </li>
            ))}
          </ol>
          <Button variant="brand" className="mt-7 w-full" onClick={() => setCreating(true)}>
            첫 모임 만들기
          </Button>
        </div>
        </Rise>
      )}

      {nextMeeting ? (
        <Rise>
        <Link
          to={`/meetings/${nextMeeting.id}`}
          className="pressable group relative block overflow-hidden rounded-[24px] bg-hero px-6 py-[22px] text-white shadow-[var(--hero-glow)] focus-visible:outline-none focus-visible:shadow-focus-ring before:absolute before:-right-10 before:-top-10 before:h-[150px] before:w-[150px] before:rounded-full before:bg-white/[0.07] after:absolute after:-bottom-12 after:right-10 after:h-[110px] after:w-[110px] after:rounded-full after:bg-white/[0.04]"
        >
          <div className="relative flex items-center justify-between gap-3">
            <span className="inline-flex items-center gap-1.5 text-[12px] font-semibold text-white/70">
              <span className="h-1.5 w-1.5 rounded-full bg-[#9ff08a]" />
              다음 모임
            </span>
            <span className="rounded-full bg-white/20 px-3 py-1 text-[12px] font-bold tabular-nums text-white">
              {dDayLabel(nextMeeting.confirmedDate as string)}
            </span>
          </div>
          <p className="relative mt-4 line-clamp-1 text-[24px] font-bold leading-tight tracking-[-0.7px] text-white">
            {nextMeeting.title}
          </p>
          <p className="relative mt-2 text-[13px] font-medium text-white/70">
            {fmtKst(nextMeeting.confirmedDate as string, 'M월 d일 (E) HH:mm')}
            {nextMeeting.region ? ` · ${nextMeeting.region}` : ''}
          </p>
          <div className="relative mt-5 flex items-center justify-between">
            <span className="text-[12.5px] font-medium text-white/60">
              {nextMeeting.participantCount}명 참여
            </span>
            <span className="inline-flex items-center gap-1 text-[12.5px] font-semibold text-white/85">
              자세히 보기
              <Icon name="right" size={15} className="transition-transform group-hover:translate-x-0.5" />
            </span>
          </div>
        </Link>
        </Rise>
      ) : null}

      {restItems.length > 0 && (
        <Rise delay={0.06}>
        <section aria-labelledby="meetings-heading">
          <div className="mb-3 flex items-baseline justify-between px-1">
            <h2 id="meetings-heading" className="text-[16px] font-bold tracking-[-0.3px] text-ink">
              모임
            </h2>
            <span className="text-[13px] font-semibold tabular-nums text-ink-faint">{restItems.length}</span>
          </div>
          <ul aria-label="모임 목록" className="overflow-hidden rounded-[20px] border border-surface-border/70 bg-surface/60 backdrop-blur-sm">
            {restItems.map((m) => (
              <li key={m.id} className="border-t border-surface-border/60 first:border-t-0">
                <Link
                  to={`/meetings/${m.id}`}
                  className="pressable flex w-full items-center gap-3 px-3.5 py-3.5 transition-colors hover:bg-surface-muted/70 focus-visible:outline-none focus-visible:shadow-focus-ring"
                >
                  <span
                    aria-hidden="true"
                    className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-[14px] ${meetingIconTone(m.status)}`}
                  >
                    <Icon name={meetingIconName(m.status)} size={20} />
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-[15px] font-semibold tracking-[-0.2px] text-ink">
                      {m.title}
                    </span>
                    <span className="mt-0.5 block truncate text-[13px] font-medium text-ink-muted">
                      {m.region ?? '지역 미정'} · {m.participantCount}명
                    </span>
                  </span>
                  <span className="shrink-0">
                    <StatusBadge status={m.status} />
                  </span>
                  <Icon name="right" size={17} className="shrink-0 text-ink-faint" />
                </Link>
              </li>
            ))}
          </ul>
        </section>
        </Rise>
      )}

      {meetingsQ.hasNextPage && (
        <div ref={sentinelRef} className="py-4 text-center text-small text-ink-muted">
          {meetingsQ.isFetchingNextPage ? '더 불러오는 중…' : '스크롤로 더 보기'}
        </div>
      )}

      {rooms.length > 0 && (
        <Rise delay={0.12}>
        <section aria-labelledby="rooms-heading">
          <div className="mb-3 flex items-baseline justify-between px-1">
            <h2 id="rooms-heading" className="text-[16px] font-bold tracking-[-0.3px] text-ink">
              모임방
            </h2>
            <Link
              to="/rooms"
              className="text-[13px] font-semibold text-primary-text transition-colors hover:text-primary-strong focus-visible:outline-none focus-visible:shadow-focus-ring"
            >
              전체 보기
            </Link>
          </div>
          <ul aria-label="모임방 목록" className="overflow-hidden rounded-[20px] border border-surface-border/70 bg-surface/60 backdrop-blur-sm">
            {rooms.slice(0, 3).map((room) => (
              <li key={room.id} className="border-t border-surface-border/60 first:border-t-0">
                <Link
                  to={`/rooms/${room.id}`}
                  className="pressable flex w-full items-center gap-3 px-3.5 py-3.5 transition-colors hover:bg-surface-muted/70 focus-visible:outline-none focus-visible:shadow-focus-ring"
                >
                  <span
                    aria-hidden="true"
                    className="flex h-11 w-11 shrink-0 items-center justify-center rounded-[14px] bg-primary-soft text-[16px] font-bold text-primary-text"
                  >
                    {room.name.trim().charAt(0) || '방'}
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-[15px] font-semibold tracking-[-0.2px] text-ink">
                      {room.name}
                    </span>
                    <span className="mt-0.5 block truncate text-[13px] font-medium text-ink-muted">
                      멤버 {room.memberCount}명 · 모임 {room.meetingCount}개
                    </span>
                  </span>
                  <Icon name="right" size={17} className="shrink-0 text-ink-faint" />
                </Link>
              </li>
            ))}
          </ul>
          <p className="mt-2.5 px-1 text-[12.5px] font-medium text-ink-faint">
            같은 친구들과 계속 모이는 방 — 채팅과 모임 기록이 한 곳에 모여요
          </p>
        </section>
        </Rise>
      )}

      <Dialog
        open={creating}
        title="새 모임 만들기"
        onCancel={() => {
          if (createMut.isPending) return;
          setCreating(false);
          setTitleError(null);
        }}
        confirmLabel={createMut.isPending ? '만드는 중…' : '만들기'}
        confirmDisabled={createMut.isPending || title.trim().length === 0}
        onConfirm={() => onSubmit({ preventDefault: () => {} } as FormEvent)}
      >
        <form onSubmit={onSubmit} className="space-y-3">
          <Input
            label="모임 제목"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="예) 금요 저녁 모임"
            maxLength={50}
            required
            aria-required="true"
            autoFocus
            error={titleError}
          />
          <div className="space-y-1.5">
            <Input
              label="지역 (선택)"
              value={region}
              onChange={(e) => setRegion(e.target.value)}
              placeholder="강남, 성수 등"
              maxLength={30}
            />
            <DragScroll
              role="group"
              aria-label="주로 가는 지역 빠른 선택"
              className="scrollbar-none -mx-1 flex gap-1.5 overflow-x-auto px-1"
            >
              {orderByFavorites(HOT_REGIONS, preferredRegions).slice(0, 8).map((r) => {
                const isSelected = region === r.name;
                const isPreferred = preferredRegions.includes(r.name);
                return (
                  <button
                    key={r.name}
                    type="button"
                    aria-pressed={isSelected}
                    onClick={() => pickRegionChip(r.name)}
                    className={`inline-flex shrink-0 items-center gap-1 rounded-full px-3 py-1 text-small font-semibold transition-all duration-150 ease-airbnb focus-visible:outline-none focus-visible:shadow-focus-ring ${
                      isSelected
                        ? 'bg-primary text-white'
                        : 'bg-surface-muted text-ink-muted hover:bg-surface-border/60'
                    }`}
                  >
                    {isPreferred && <Icon name="heart" size={13} />}
                    {r.name}
                  </button>
                );
              })}
            </DragScroll>
            <p className="px-1 text-tag text-ink-muted">
              {preferredRegions.length > 0
                ? '선호 표시는 내가 자주 가는 지역 — 장소 검색 때 맨 앞에 떠요'
                : '지역을 탭하면 선호 지역으로 등록돼요'}
            </p>
          </div>
        </form>
      </Dialog>
    </section>
  );
}
